:root {
  color: #20242a;
  background: #f7f8fa;
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", "Microsoft YaHei", sans-serif;
  font-synthesis: none;
  text-rendering: optimizeLegibility;
  --bg: #f7f8fa;
  --surface: #ffffff;
  --surface-soft: #f0f2f5;
  --border: #dfe3e8;
  --border-strong: #cbd1d8;
  --text: #20242a;
  --muted: #69717c;
  --accent: #167a62;
  --accent-hover: #0f6652;
  --accent-soft: #e4f3ee;
  --danger: #b43737;
  --danger-soft: #fbeaea;
  --shadow: 0 16px 44px rgba(22, 29, 37, 0.12);
}

* { box-sizing: border-box; }

body {
  margin: 0;
  min-width: 320px;
  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
  letter-spacing: 0;
}

button, input { font: inherit; letter-spacing: 0; }
button { color: inherit; }
button:focus-visible, input:focus-visible, [tabindex]:focus-visible {
  outline: 3px solid rgba(22, 122, 98, 0.22);
  outline-offset: 2px;
}

.centered-page, .login-page {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 28px;
}

.centered-page { align-content: center; gap: 18px; }
.muted { color: var(--muted); }

.login-page {
  background:
    linear-gradient(rgba(255,255,255,0.74), rgba(255,255,255,0.74)),
    repeating-linear-gradient(90deg, transparent 0 47px, rgba(32,36,42,0.045) 48px),
    repeating-linear-gradient(0deg, transparent 0 47px, rgba(32,36,42,0.045) 48px),
    var(--bg);
}

.login-panel {
  width: min(100%, 410px);
  padding: 30px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: rgba(255,255,255,0.96);
  box-shadow: var(--shadow);
}

.brand-row { display: flex; align-items: center; gap: 10px; font-size: 17px; font-weight: 750; }
.brand-row.compact { font-size: 16px; }
.brand-mark {
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border-radius: 7px;
  color: white;
  background: var(--accent);
}
.compact .brand-mark { width: 34px; height: 34px; }

.login-copy { margin: 34px 0 24px; }
.login-copy h1 { margin: 0 0 8px; font-size: 25px; line-height: 1.2; }
.login-copy p { margin: 0; color: var(--muted); font-size: 14px; line-height: 1.7; }
.login-form { display: grid; gap: 10px; }
.login-form label { font-size: 13px; font-weight: 700; }
.password-field, .search-field {
  display: flex;
  align-items: center;
  border: 1px solid var(--border-strong);
  border-radius: 7px;
  background: var(--surface);
}
.password-field { height: 46px; padding: 0 13px; gap: 10px; color: var(--muted); }
.password-field:focus-within, .search-field:focus-within { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(22,122,98,0.1); }
.password-field input, .search-field input { width: 100%; border: 0; outline: 0; background: transparent; color: var(--text); }
.form-error { margin: 0; color: var(--danger); font-size: 13px; }

.primary-button, .secondary-button, .danger-button {
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 16px;
  border-radius: 7px;
  border: 1px solid transparent;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
}
.primary-button { background: var(--accent); color: white; }
.primary-button:hover:not(:disabled) { background: var(--accent-hover); }
.secondary-button { background: var(--surface); border-color: var(--border-strong); }
.secondary-button:hover:not(:disabled) { background: var(--surface-soft); }
.danger-button { background: var(--danger); color: white; }
.danger-button:hover:not(:disabled) { background: #962c2c; }
.primary-button:disabled, .secondary-button:disabled, .danger-button:disabled, .icon-button:disabled { opacity: 0.55; cursor: not-allowed; }
.full-button { width: 100%; margin-top: 6px; }

.topbar { position: sticky; top: 0; z-index: 20; border-bottom: 1px solid var(--border); background: rgba(255,255,255,0.92); backdrop-filter: blur(12px); }
.topbar-inner { width: min(100% - 40px, 1180px); height: 64px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; }
.topbar-actions, .toolbar-meta, .card-actions { display: flex; align-items: center; gap: 8px; }
.storage-badge { display: inline-flex; align-items: center; gap: 7px; color: var(--muted); font-size: 12px; font-weight: 700; }
.storage-badge span { width: 7px; height: 7px; border-radius: 50%; background: #2aa37f; box-shadow: 0 0 0 3px var(--accent-soft); }
.icon-button { width: 36px; height: 36px; display: inline-grid; place-items: center; padding: 0; border: 1px solid transparent; border-radius: 7px; background: transparent; cursor: pointer; }
.icon-button:hover:not(:disabled) { border-color: var(--border); background: var(--surface-soft); }

.library-page { width: min(100% - 40px, 1180px); margin: 0 auto; padding: 42px 0 70px; }
.page-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; margin-bottom: 24px; }
.eyebrow { margin: 0 0 6px; color: var(--accent); font-size: 12px; font-weight: 800; text-transform: uppercase; }
.page-heading h1 { margin: 0; font-size: 28px; line-height: 1.15; }

.upload-zone {
  min-height: 132px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  border: 1px dashed var(--border-strong);
  border-radius: 8px;
  background: var(--surface);
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease;
}
.upload-zone:hover, .upload-zone.is-dragging { border-color: var(--accent); background: #f4fbf8; }
.upload-icon { width: 48px; height: 48px; display: grid; place-items: center; border-radius: 8px; color: var(--accent); background: var(--accent-soft); flex: 0 0 auto; }
.upload-zone strong { display: block; margin-bottom: 6px; font-size: 15px; }
.upload-zone p { margin: 0; color: var(--muted); font-size: 13px; line-height: 1.5; }

.library-toolbar { min-height: 68px; display: flex; align-items: center; justify-content: space-between; gap: 16px; border-bottom: 1px solid var(--border); }
.search-field { width: min(100%, 330px); height: 38px; padding: 0 10px; gap: 8px; color: var(--muted); }
.search-field input { font-size: 13px; }
.search-field button { width: 27px; height: 27px; display: grid; place-items: center; border: 0; border-radius: 5px; background: transparent; color: var(--muted); cursor: pointer; }
.search-field button:hover { background: var(--surface-soft); }
.toolbar-meta { color: var(--muted); font-size: 13px; }

.image-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; padding-top: 22px; }
.image-card { min-width: 0; overflow: visible; border: 1px solid var(--border); border-radius: 7px; background: var(--surface); }
.thumbnail-button { position: relative; width: 100%; aspect-ratio: 4 / 3; display: block; overflow: hidden; padding: 0; border: 0; border-radius: 6px 6px 0 0; background: #e9ecf0; cursor: zoom-in; }
.thumbnail-button img { width: 100%; height: 100%; display: block; object-fit: cover; transition: transform 180ms ease; }
.thumbnail-button:hover img { transform: scale(1.02); }
.preview-hint { position: absolute; right: 10px; bottom: 10px; display: flex; align-items: center; gap: 5px; padding: 6px 8px; border-radius: 6px; background: rgba(19,23,28,0.76); color: white; font-size: 11px; font-weight: 700; opacity: 0; transform: translateY(3px); transition: opacity 160ms ease, transform 160ms ease; }
.thumbnail-button:hover .preview-hint, .thumbnail-button:focus-visible .preview-hint { opacity: 1; transform: translateY(0); }
.image-card-body { min-height: 68px; display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 10px 9px 10px 12px; }
.image-info { min-width: 0; display: grid; gap: 4px; }
.image-info strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; }
.image-info span { color: var(--muted); font-size: 11px; white-space: nowrap; }
.menu-wrap { position: relative; }
.action-menu { position: absolute; right: 0; top: 42px; z-index: 12; width: 175px; padding: 5px; border: 1px solid var(--border); border-radius: 7px; background: var(--surface); box-shadow: var(--shadow); }
.action-menu button { width: 100%; min-height: 36px; display: flex; align-items: center; gap: 9px; padding: 0 10px; border: 0; border-radius: 5px; background: transparent; text-align: left; font-size: 13px; cursor: pointer; }
.action-menu button:hover { background: var(--surface-soft); }
.action-menu .danger-action { color: var(--danger); }

.image-skeleton { aspect-ratio: 4 / 3; border-radius: 7px; background: linear-gradient(100deg, #e8ebef 30%, #f3f5f7 45%, #e8ebef 60%); background-size: 220% 100%; animation: shimmer 1.3s infinite linear; }
.empty-state { min-height: 330px; display: grid; justify-items: center; align-content: center; text-align: center; }
.empty-icon { width: 52px; height: 52px; display: grid; place-items: center; border-radius: 8px; color: var(--muted); background: var(--surface-soft); }
.empty-state h2 { margin: 16px 0 7px; font-size: 17px; }
.empty-state p { margin: 0 0 18px; color: var(--muted); font-size: 13px; }
.load-more-row { display: flex; justify-content: center; padding-top: 28px; }

.upload-status { position: fixed; right: 22px; bottom: 22px; z-index: 35; width: min(330px, calc(100vw - 44px)); padding: 14px; border: 1px solid var(--border); border-radius: 8px; background: var(--surface); box-shadow: var(--shadow); }
.upload-status > strong { display: block; margin-bottom: 8px; font-size: 12px; }
.upload-row { min-height: 30px; display: flex; align-items: center; gap: 8px; color: var(--muted); font-size: 12px; }
.upload-row span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.upload-row.done { color: var(--accent); }
.upload-row.error { color: var(--danger); }
.toast { position: fixed; left: 50%; bottom: 24px; z-index: 60; min-height: 42px; max-width: calc(100vw - 32px); display: flex; align-items: center; gap: 9px; padding: 0 10px 0 14px; border: 1px solid #343a41; border-radius: 7px; background: #252a30; color: white; box-shadow: var(--shadow); transform: translateX(-50%); font-size: 13px; }
.toast.success { border-color: #176b57; background: #125b49; }
.toast.error { border-color: #9f3030; background: #812727; }
.toast button { width: 27px; height: 27px; display: grid; place-items: center; border: 0; border-radius: 5px; background: transparent; color: inherit; cursor: pointer; }
.toast button:hover { background: rgba(255,255,255,0.1); }

.dialog-backdrop { position: fixed; inset: 0; z-index: 50; display: grid; place-items: center; padding: 20px; background: rgba(18,21,25,0.66); backdrop-filter: blur(6px); }
.preview-dialog { width: min(980px, 100%); max-height: calc(100vh - 40px); display: grid; grid-template-rows: auto minmax(0, 1fr) auto; overflow: hidden; border-radius: 8px; background: var(--surface); box-shadow: var(--shadow); }
.preview-dialog header, .preview-dialog footer { min-height: 66px; display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 12px 16px; }
.preview-dialog header { border-bottom: 1px solid var(--border); }
.preview-dialog header > div { min-width: 0; display: grid; gap: 4px; }
.preview-dialog header strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 14px; }
.preview-dialog header span { color: var(--muted); font-size: 12px; }
.preview-canvas { min-height: 280px; display: grid; place-items: center; overflow: auto; padding: 20px; background: #15191e; }
.preview-canvas img { max-width: 100%; max-height: calc(100vh - 190px); object-fit: contain; }
.preview-dialog footer { border-top: 1px solid var(--border); }
.link-field { min-width: 0; flex: 1; height: 40px; display: flex; align-items: center; padding: 0 12px; overflow: hidden; border: 1px solid var(--border); border-radius: 7px; background: var(--surface-soft); }
.link-field span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--muted); font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }

.confirm-dialog { width: min(440px, 100%); display: grid; grid-template-columns: auto 1fr; gap: 14px; padding: 22px; border-radius: 8px; background: var(--surface); box-shadow: var(--shadow); }
.danger-icon { width: 42px; height: 42px; display: grid; place-items: center; border-radius: 7px; color: var(--danger); background: var(--danger-soft); }
.confirm-dialog h2 { margin: 1px 0 8px; font-size: 18px; }
.confirm-dialog p { margin: 0; color: var(--muted); font-size: 13px; line-height: 1.6; overflow-wrap: anywhere; }
.confirm-actions { grid-column: 1 / -1; display: flex; justify-content: flex-end; gap: 8px; padding-top: 7px; }

.spin { animation: spin 850ms linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
@keyframes shimmer { to { background-position-x: -220%; } }

@media (max-width: 920px) {
  .image-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

@media (max-width: 680px) {
  .topbar-inner, .library-page { width: min(100% - 28px, 1180px); }
  .library-page { padding-top: 28px; }
  .page-heading { align-items: center; }
  .page-heading h1 { font-size: 24px; }
  .page-heading .primary-button { width: 42px; padding: 0; }
  .page-heading .primary-button svg { margin: 0; }
  .page-heading .primary-button { font-size: 0; }
  .upload-zone { min-height: 122px; justify-content: flex-start; padding: 20px; }
  .library-toolbar { align-items: stretch; flex-direction: column; justify-content: center; padding: 14px 0; }
  .search-field { width: 100%; }
  .toolbar-meta { justify-content: space-between; }
  .image-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; padding-top: 14px; }
  .image-card-body { min-height: 62px; padding-left: 9px; }
  .image-info span { font-size: 10px; }
  .storage-badge { display: none; }
  .preview-dialog footer { align-items: stretch; flex-direction: column; }
  .preview-dialog footer .primary-button { width: 100%; }
  .link-field { flex: 0 0 40px; }
  .upload-status { right: 14px; bottom: 14px; width: calc(100vw - 28px); }
}

@media (max-width: 430px) {
  .login-page { padding: 16px; }
  .login-panel { padding: 24px 20px; }
  .upload-zone { gap: 12px; }
  .upload-icon { width: 42px; height: 42px; }
  .upload-zone p { font-size: 11px; }
  .thumbnail-button { aspect-ratio: 1 / 1; }
  .card-actions .icon-button:first-child { display: none; }
  .confirm-dialog { padding: 18px; }
}

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { scroll-behavior: auto !important; transition-duration: 0.01ms !important; animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; }
}
